/**
 * Phase 5 — connectivity helper.
 *
 * Native shell -> @capacitor/network (reliable on iOS/Android).
 * Browser/PWA  -> navigator.onLine + online/offline events.
 */
import { isNativeApp } from '../platform';

export async function isOnline(): Promise<boolean> {
  if (isNativeApp()) {
    try {
      const { Network } = await import('@capacitor/network');
      const status = await Network.getStatus();
      return status.connected;
    } catch {
      return true;
    }
  }
  try {
    return navigator.onLine !== false;
  } catch {
    return true;
  }
}

/** Subscribe to connectivity changes. Returns an unsubscribe function. */
export function onConnectivityChange(cb: (online: boolean) => void): () => void {
  if (isNativeApp()) {
    let remove: (() => void) | undefined;
    void (async () => {
      try {
        const { Network } = await import('@capacitor/network');
        const handle = await Network.addListener('networkStatusChange', (s) => cb(s.connected));
        remove = () => handle.remove();
      } catch {
        /* ignore */
      }
    })();
    return () => remove?.();
  }

  const on = () => cb(true);
  const off = () => cb(false);
  window.addEventListener('online', on);
  window.addEventListener('offline', off);
  return () => {
    window.removeEventListener('online', on);
    window.removeEventListener('offline', off);
  };
}
